import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  List,
  Tag,
  Space,
  Divider,
  Alert,
  Upload,
  Select,
  Input,
  Switch,
  Steps
} from 'antd';
import { UploadOutlined, FileTextOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const { Step } = Steps;

const TransactionImportPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [fileData, setFileData] = useState(null);
  const [brokerFormat, setBrokerFormat] = useState('GENERIC');
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const [autoDetect, setAutoDetect] = useState(true);
  const [fieldMappings, setFieldMappings] = useState({});
  const [availableColumns, setAvailableColumns] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFileUpload = (info) => {
    const { file } = info;
    setFileData(file);
    setCurrentStep(1);
  };

  const handleConfigureContinue = async () => {
    if (!fileData) return;
    
    setLoading(true);
    try {
      // For web, we would need to implement file parsing
      // For now, just move to next step
      setCurrentStep(2);
    } catch (error) {
      alert(`Failed to process file: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImportComplete = (results) => {
    console.log('Import completed:', results);
  };

  const supportedFormats = [
    { name: 'CSV files', extension: '.csv', description: 'Comma-separated values' },
    { name: 'Excel files', extension: '.xlsx, .xls', description: 'Microsoft Excel spreadsheets' },
    { name: 'PDF files', extension: '.pdf', description: 'Portable Document Format' }
  ];

  const brokerFormats = [
    { name: 'Interactive Brokers', description: 'Professional trading platform' },
    { name: 'TD Ameritrade', description: 'Popular US broker' },
    { name: 'Fidelity', description: 'Investment and retirement services' },
    { name: 'Robinhood', description: 'Commission-free trading' },
    { name: 'Generic format', description: 'Standard CSV/Excel format' }
  ];

  const requiredFields = [
    { field: 'Date', description: 'Transaction date' },
    { field: 'Symbol/Ticker', description: 'Stock symbol (e.g., AAPL)' },
    { field: 'Transaction Type', description: 'Buy, Sell, Dividend, etc.' },
    { field: 'Quantity/Shares', description: 'Number of shares' },
    { field: 'Price per share', description: 'Price per unit' }
  ];

  const optionalFields = [
    { field: 'Total Amount', description: 'Total transaction value' },
    { field: 'Fees/Commission', description: 'Transaction costs' },
    { field: 'Currency', description: 'Transaction currency' },
    { field: 'Notes', description: 'Additional information' }
  ];

  const tips = [
    'Ensure your file has column headers in the first row',
    'Use consistent date formats (MM/DD/YYYY, DD/MM/YYYY, or YYYY-MM-DD)',
    'Include stock symbols in standard format (e.g., AAPL, MSFT)',
    'Check for empty rows or missing data before importing',
    'Use positive numbers for quantities and prices',
    'Include transaction fees if available for accurate calculations'
  ];

  const steps = [
    {
      title: 'Upload File',
      description: 'Select your transaction file'
    },
    {
      title: 'Configure',
      description: 'Set import parameters'
    },
    {
      title: 'Map Fields',
      description: 'Match columns to fields'
    },
    {
      title: 'Preview',
      description: 'Review transactions'
    },
    {
      title: 'Import',
      description: 'Complete import'
    }
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <Title level={1}>Import Transactions</Title>
        <Paragraph style={{ fontSize: '18px', color: '#666' }}>
          Import your transaction history from CSV, XLSX, or PDF files
        </Paragraph>
      </div>

      <Steps current={currentStep} style={{ marginBottom: '40px' }}>
        {steps.map((step, index) => (
          <Step key={index} title={step.title} description={step.description} />
        ))}
      </Steps>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card title="Import Your Data" style={{ marginBottom: '24px' }}>
            {currentStep === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <UploadOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
                <Title level={3}>Upload Your File</Title>
                <Paragraph type="secondary" style={{ marginBottom: '24px' }}>
                  Upload your transaction file and we'll automatically detect the format and import your data.
                </Paragraph>
                <Upload.Dragger
                  accept=".csv,.xlsx,.xls,.pdf"
                  beforeUpload={() => false}
                  onChange={handleFileUpload}
                  style={{ marginBottom: '16px' }}
                >
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag file to this area to upload</p>
                  <p className="ant-upload-hint">
                    Supports CSV, XLSX, and PDF files up to 10MB
                  </p>
                </Upload.Dragger>
              </div>
            )}

            {currentStep === 1 && (
              <div style={{ padding: '20px' }}>
                <Title level={4}>Configure Import Settings</Title>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Broker Format:</Text>
                    <Select
                      value={brokerFormat}
                      onChange={setBrokerFormat}
                      style={{ width: '100%', marginTop: '8px' }}
                    >
                      <Select.Option value="GENERIC">Generic</Select.Option>
                      <Select.Option value="INTERACTIVE_BROKERS">Interactive Brokers</Select.Option>
                      <Select.Option value="TD_AMERITRADE">TD Ameritrade</Select.Option>
                      <Select.Option value="SCHWAB">Schwab</Select.Option>
                    </Select>
                  </div>
                  <div>
                    <Text strong>Default Currency:</Text>
                    <Input
                      value={defaultCurrency}
                      onChange={(e) => setDefaultCurrency(e.target.value)}
                      style={{ marginTop: '8px' }}
                      maxLength={3}
                    />
                  </div>
                  <div>
                    <Space>
                      <Switch checked={autoDetect} onChange={setAutoDetect} />
                      <Text>Auto-detect field mappings</Text>
                    </Space>
                  </div>
                </Space>
                <div style={{ marginTop: '24px', textAlign: 'right' }}>
                  <Space>
                    <Button onClick={() => setCurrentStep(0)}>Back</Button>
                    <Button type="primary" onClick={handleConfigureContinue} loading={loading}>
                      Continue
                    </Button>
                  </Space>
                </div>
              </div>
            )}
          </Card>

          <Card title="Supported File Formats" style={{ marginBottom: '24px' }}>
            <List
              dataSource={supportedFormats}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<FileTextOutlined style={{ color: '#1890ff' }} />}
                    title={
                      <Space>
                        <Text strong>{item.name}</Text>
                        <Tag color="blue">{item.extension}</Tag>
                      </Space>
                    }
                    description={item.description}
                  />
                </List.Item>
              )}
            />
          </Card>

          <Card title="Supported Broker Formats" style={{ marginBottom: '24px' }}>
            <List
              dataSource={brokerFormats}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    title={item.name}
                    description={item.description}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Required Fields" style={{ marginBottom: '24px' }}>
            <List
              size="small"
              dataSource={requiredFields}
              renderItem={(item) => (
                <List.Item>
                  <Text strong>{item.field}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {item.description}
                  </Text>
                </List.Item>
              )}
            />
          </Card>

          <Card title="Optional Fields" style={{ marginBottom: '24px' }}>
            <List
              size="small"
              dataSource={optionalFields}
              renderItem={(item) => (
                <List.Item>
                  <Text>{item.field}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {item.description}
                  </Text>
                </List.Item>
              )}
            />
          </Card>

          <Card title="Tips for Best Results">
            <List
              size="small"
              dataSource={tips}
              renderItem={(item) => (
                <List.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    • {item}
                  </Text>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      <Alert
        message="Data Privacy"
        description="Your transaction data is processed locally and securely stored in your portfolio. We never share your financial information with third parties."
        type="info"
        showIcon
        style={{ marginTop: '24px' }}
      />
    </div>
  );
};

export default TransactionImportPage;
